"use client";

import type { Question } from "./quiz-data";

type Props = {
  question: Question;
  total: number;
  index: number;
  selectedAnswerId?: string;
  onSelect: (answerId: string) => void;
  onBack: () => void;
};

export function QuestionScreen({
  question,
  total,
  index,
  selectedAnswerId,
  onSelect,
  onBack,
}: Props) {
  const progress = Math.round(((index + 1) / total) * 100);
  return (
    <div className="min-h-[calc(100vh-80px)] flex flex-col px-6 md:px-10 pt-8 md:pt-12 pb-8 animate-fade-in">
      <div className="max-w-[720px] w-full mx-auto flex-1 flex flex-col">
        <div className="mb-8 md:mb-12">
          <div className="flex items-center justify-between mb-2">
            <span className="text-[11px] font-semibold uppercase tracking-[2px] text-gold">
              Question {index + 1} of {total}
            </span>
            <button
              type="button"
              onClick={onBack}
              disabled={index === 0}
              className="text-[12px] text-text-muted hover:text-gold disabled:opacity-40 disabled:hover:text-text-muted transition-colors"
              aria-label="Back to previous question"
            >
              ← Back
            </button>
          </div>
          <div className="h-1 bg-border rounded-full overflow-hidden">
            <div
              className="h-full bg-gold transition-all duration-500 ease-out"
              style={{ width: `${progress}%` }}
              aria-hidden
            />
          </div>
        </div>

        <h1 className="font-serif text-[30px] md:text-[44px] leading-[1.15] text-navy mb-2">
          {question.prompt}
        </h1>
        {question.hint ? (
          <p className="text-[13px] text-text-muted mb-6">{question.hint}</p>
        ) : (
          <div className="mb-6" />
        )}

        <div className="grid gap-3 md:gap-4">
          {question.answers.map((a) => {
            const selected = a.id === selectedAnswerId;
            return (
              <button
                key={a.id}
                type="button"
                onClick={() => onSelect(a.id)}
                className={[
                  "w-full text-left rounded-[14px] border-[1.5px] p-5 md:p-6 transition-all min-h-[64px]",
                  "hover:-translate-y-0.5 hover:shadow-[0_8px_24px_rgba(0,0,0,0.06)]",
                  selected
                    ? "border-gold bg-gold/[0.04]"
                    : "border-border-light bg-white hover:border-gold/40",
                ].join(" ")}
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="min-w-0">
                    <p className="text-[16px] md:text-[17px] font-semibold text-navy">
                      {a.label}
                    </p>
                    {a.subLabel ? (
                      <p className="text-[13px] md:text-[14px] text-text-muted mt-1 leading-[1.5]">
                        {a.subLabel}
                      </p>
                    ) : null}
                  </div>
                  {selected ? (
                    <span className="shrink-0 text-gold text-[20px]" aria-hidden>
                      ✓
                    </span>
                  ) : null}
                </div>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}
